import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Button from '../../../components/Button';
import { ROUTE_BACK_OFFICE_CONTENT_VALIDATORS } from '../../../constants';
import { showErrorMessage, showSuccessMessage } from '@/shared/components/toast-system';
import * as adminApi from '../../../utils/contentValidatorAdminApiUtils';

import './ContentValidatorEdit.css';

const EMPTY = {
  name: '',
  description: '',
  instruction: '',
  provider: '',
  model: '',
  temperature: '',
  maxTokens: '',
  topP: '',
  frequencyPenalty: '',
  presencePenalty: '',
  fallbackProvider: '',
  fallbackModel: '',
  isActive: true,
  sortOrder: '',
};

const NUMERIC_FIELDS = ['temperature', 'maxTokens', 'topP', 'frequencyPenalty', 'presencePenalty', 'sortOrder'];
const INTEGER_FIELDS = ['maxTokens', 'sortOrder'];

const toFormValue = (value) => (value === null || value === undefined ? '' : value);

const modelLabel = (m) => {
  if (!m) return '';
  const context = m.maxInputTokens ? ` (${Math.round(m.maxInputTokens / 1000)}K context)` : '';
  return `${m.name || m.id}${context}`;
};

const buildPayload = (form) => {
  const payload = {
    name: form.name?.trim() || '',
    description: form.description?.trim() || null,
    instruction: form.instruction ?? '',
    provider: form.provider || null,
    model: form.model || null,
    fallbackProvider: form.fallbackProvider || null,
    fallbackModel: form.fallbackModel || null,
    isActive: !!form.isActive,
  };

  NUMERIC_FIELDS.forEach((field) => {
    const raw = form[field];
    if (raw === '' || raw === null || raw === undefined) {
      payload[field] = null;
    } else {
      const parsed = INTEGER_FIELDS.includes(field) ? parseInt(raw, 10) : parseFloat(raw);
      payload[field] = Number.isFinite(parsed) ? parsed : null;
    }
  });

  return payload;
};

const ContentValidatorEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;

  const [form, setForm] = useState(EMPTY);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);

  const [providers, setProviders] = useState([]);
  const [models, setModels] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOptionsLoading(true);
      try {
        const options = await adminApi.getContentValidatorModelOptions();
        if (cancelled) return;
        setProviders(Array.isArray(options?.providers) ? options.providers : []);
        setModels(Array.isArray(options?.models) ? options.models : []);
      } catch {
        if (!cancelled) showErrorMessage('Failed to load model options.');
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (isNew) return undefined;

    (async () => {
      setIsLoading(true);
      try {
        const record = await adminApi.getContentValidatorById(id);
        if (cancelled || !record) return;
        setForm({
          name: toFormValue(record.name),
          description: toFormValue(record.description),
          instruction: toFormValue(record.instruction),
          provider: toFormValue(record.provider),
          model: toFormValue(record.model),
          temperature: toFormValue(record.temperature),
          maxTokens: toFormValue(record.maxTokens),
          topP: toFormValue(record.topP),
          frequencyPenalty: toFormValue(record.frequencyPenalty),
          presencePenalty: toFormValue(record.presencePenalty),
          fallbackProvider: toFormValue(record.fallbackProvider),
          fallbackModel: toFormValue(record.fallbackModel),
          isActive: record.isActive !== false,
          sortOrder: toFormValue(record.sortOrder),
        });
      } catch {
        showErrorMessage('Failed to load the content validator.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [id, isNew]);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const providerOptions = useMemo(() => {
    const opts = [...providers];
    [form.provider, form.fallbackProvider].forEach((val) => {
      if (val && !opts.some((p) => p.id === val)) {
        opts.push({ id: val, name: `${val} (current)` });
      }
    });
    return opts;
  }, [providers, form.provider, form.fallbackProvider]);

  const modelsFor = useCallback((providerId, currentValue) => {
    const opts = models.filter((m) => m.providerId === providerId);
    if (currentValue && !opts.some((m) => m.id === currentValue)) {
      return [{ id: currentValue, name: `${currentValue} (current)` }, ...opts];
    }
    return opts;
  }, [models]);

  const primaryModelOptions = useMemo(
    () => modelsFor(form.provider, form.model),
    [modelsFor, form.provider, form.model]
  );
  const fallbackModelOptions = useMemo(
    () => modelsFor(form.fallbackProvider, form.fallbackModel),
    [modelsFor, form.fallbackProvider, form.fallbackModel]
  );

  const canSave = useMemo(() => {
    const baseOk =
      form.name.trim().length > 0 &&
      form.instruction.trim().length > 0 &&
      !!form.provider &&
      !!form.model;
    const fallbackOk = !form.fallbackProvider || !!form.fallbackModel;
    return baseOk && fallbackOk;
  }, [form.name, form.instruction, form.provider, form.model, form.fallbackProvider, form.fallbackModel]);

  const handleSave = async () => {
    if (!canSave) {
      if (form.fallbackProvider && !form.fallbackModel) {
        showErrorMessage('Select a fallback model, or clear the fallback provider.');
      } else {
        showErrorMessage('Name, instruction, provider, and model are required.');
      }
      return;
    }
    setIsSaving(true);
    try {
      const payload = buildPayload(form);
      if (isNew) {
        await adminApi.createContentValidator(payload);
        showSuccessMessage('Content validator created.');
      } else {
        await adminApi.updateContentValidator(id, payload);
        showSuccessMessage('Content validator updated.');
      }
      navigate(ROUTE_BACK_OFFICE_CONTENT_VALIDATORS);
    } catch (error) {
      showErrorMessage(error?.message || 'Failed to save the content validator.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => navigate(ROUTE_BACK_OFFICE_CONTENT_VALIDATORS);

  if (isLoading) {
    return <div className="cve-loading">Loading…</div>;
  }

  const textField = (name, label, { placeholder } = {}) => (
    <div className="cve-field">
      <label className="cve-label" htmlFor={`cve-${name}`}>{label}</label>
      <input
        id={`cve-${name}`}
        className="cve-input"
        type="text"
        value={form[name]}
        placeholder={placeholder || ''}
        onChange={(e) => setField(name, e.target.value)}
      />
    </div>
  );

  const numberField = (name, label, { step, placeholder } = {}) => (
    <div className="cve-field">
      <label className="cve-label" htmlFor={`cve-${name}`}>{label}</label>
      <input
        id={`cve-${name}`}
        className="cve-input"
        type="number"
        step={step ?? 'any'}
        value={form[name]}
        placeholder={placeholder || ''}
        onChange={(e) => setField(name, e.target.value)}
      />
    </div>
  );

  return (
    <div className="cve-container">
      <div className="cve-header">
        <h2 className="cve-heading">{isNew ? 'New Content Validator' : 'Edit Content Validator'}</h2>
      </div>

      <div className="cve-section">
        <div className="cve-section-title">Definition</div>
        {textField('name', 'Name', { placeholder: 'e.g. No fluff' })}
        {textField('description', 'Description', { placeholder: 'Short summary shown in the validator list' })}
        <div className="cve-field">
          <label className="cve-label" htmlFor="cve-instruction">Instruction</label>
          <textarea
            id="cve-instruction"
            className="cve-textarea"
            rows={8}
            value={form.instruction}
            placeholder="The validation rule. Describe what should FAIL and what to report."
            onChange={(e) => setField('instruction', e.target.value)}
          />
          <div className="cve-hint">
            This is wrapped automatically with output-format instructions, so just describe the rule —
            you don't need to ask for JSON.
          </div>
        </div>
        <div className="cve-field cve-field-inline">
          <label className="cve-checkbox">
            <input
              type="checkbox"
              checked={!!form.isActive}
              onChange={(e) => setField('isActive', e.target.checked)}
            />
            <span>Active</span>
          </label>
          <div className="cve-field cve-field-narrow">
            <label className="cve-label" htmlFor="cve-sortOrder">Sort order</label>
            <input
              id="cve-sortOrder"
              className="cve-input"
              type="number"
              step="1"
              value={form.sortOrder}
              onChange={(e) => setField('sortOrder', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="cve-section">
        <div className="cve-section-title">Model configuration</div>

        <div className="cve-grid">
          <div className="cve-field">
            <label className="cve-label" htmlFor="cve-provider">
              Provider <span className="cve-req">*</span>
            </label>
            <select
              id="cve-provider"
              className="cve-input"
              value={form.provider}
              disabled={optionsLoading}
              onChange={(e) => setForm((prev) => ({ ...prev, provider: e.target.value, model: '' }))}
            >
              <option value="">{optionsLoading ? 'Loading…' : 'Select a provider…'}</option>
              {providerOptions.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div className="cve-field">
            <label className="cve-label" htmlFor="cve-model">
              Model <span className="cve-req">*</span>
            </label>
            <select
              id="cve-model"
              className="cve-input"
              value={form.model}
              disabled={optionsLoading || !form.provider}
              onChange={(e) => setField('model', e.target.value)}
            >
              <option value="">{form.provider ? 'Select a model…' : 'Select a provider first'}</option>
              {primaryModelOptions.map((m) => (
                <option key={m.id} value={m.id}>{modelLabel(m)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="cve-grid">
          <div className="cve-field">
            <label className="cve-label" htmlFor="cve-fallbackProvider">Fallback provider</label>
            <select
              id="cve-fallbackProvider"
              className="cve-input"
              value={form.fallbackProvider}
              disabled={optionsLoading}
              onChange={(e) => setForm((prev) => ({ ...prev, fallbackProvider: e.target.value, fallbackModel: '' }))}
            >
              <option value="">None (no fallback)</option>
              {providerOptions.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <div className="cve-hint">Used automatically if the primary model call fails.</div>
          </div>

          <div className="cve-field">
            <label className="cve-label" htmlFor="cve-fallbackModel">Fallback model</label>
            <select
              id="cve-fallbackModel"
              className="cve-input"
              value={form.fallbackModel}
              disabled={optionsLoading || !form.fallbackProvider}
              onChange={(e) => setField('fallbackModel', e.target.value)}
            >
              <option value="">{form.fallbackProvider ? 'Select a fallback model…' : 'No fallback'}</option>
              {fallbackModelOptions.map((m) => (
                <option key={m.id} value={m.id}>{modelLabel(m)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="cve-grid">
          {numberField('temperature', 'Temperature')}
          {numberField('maxTokens', 'Max tokens', { step: '1' })}
          {numberField('topP', 'Top P')}
          {numberField('frequencyPenalty', 'Frequency penalty')}
          {numberField('presencePenalty', 'Presence penalty')}
        </div>
      </div>

      <div className="cve-footer">
        <Button className="secondary" onClick={handleCancel} disabled={isSaving}>Cancel</Button>
        <Button className="primary" onClick={handleSave} disabled={isSaving || !canSave}>
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

export default ContentValidatorEdit;